import { useContext, useEffect, useRef } from 'react';

import CardContext from 'contexts/CardContext';
import {
  InsightsContentContext,
  InsightsGetValuesOptions,
} from 'contextProviders/InsightsContentProvider';
import InsightsViewScopeContext from 'contextProviders/InsightsContentProvider/InsightsViewScopeContext';
import {
  InsightsGadgetValues,
  InsightsValuesSource,
} from 'contextProviders/InsightsContentProvider/viewTypes';

// Shared registration mechanics for the two places a gadget's rendered figures
// can be read (see the sibling hooks). Registers a LAZY getter rather than
// pushing values into state: gadget data settles on every refetch, and routing
// that through React would re-render the page for numbers nobody has asked for.
// Nothing is computed unless a question is actually sent.
//
// No-ops wherever there's no Insights view scope — the Explore modal, gadget
// previews and Storybook all render gadgets outside a report.
const useRegisterValues = ({
  source,
  getValues,
}: {
  source: InsightsValuesSource;
  // `opts.pinnedLabels` arrives only when this card is the one the user asked
  // about; the digest must then include those rows even if sampled out.
  getValues: (opts?: InsightsGetValuesOptions) => InsightsGadgetValues;
}): void => {
  const { entityId } = useContext(InsightsViewScopeContext);
  const { i: cardId } = useContext(CardContext);
  const { registerViewValues } = useContext(InsightsContentContext);

  // The getter is registered once and reads through this ref, so a settling
  // refetch never has to re-register.
  const getValuesRef = useRef(getValues);
  getValuesRef.current = getValues;

  useEffect(() => {
    if (!entityId || !cardId) {
      return undefined;
    }
    return registerViewValues({
      entityId,
      cardId,
      source,
      getValues: (opts) => getValuesRef.current(opts),
    });
  }, [entityId, cardId, source, registerViewValues]);
};

export default useRegisterValues;
